import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RECURRING_INTERVALS, RECURRING_MODES } from '@makila/shared';
import { AlertTriangle, BellRing, CalendarClock, Clock, Mail, Pause, Play, Repeat, Send, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import {
  Alert, Badge, Button, Card, Empty, Field, Input, Loading, Modal, PageHeader, Select, Switch, Table, Td, Th, errorText, fieldErrors, useConfirm, useToast,
} from '../components/ui';
import { del, get, patch, post, put } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, dateTime, docLabel, mt } from '../lib/format';

// ═══ Preferências (Definições → Automação) ══════════════════════════════════

interface Settings { autoDunning: boolean; dunningDays: number[]; quoteFollowup: boolean; quoteFollowupDays: number[]; digestFrequency: 'off' | 'daily' | 'weekly'; stockAlerts: boolean }

const DUNNING_OPTIONS = [-7, -3, -1, 0, 3, 7, 15, 30, 60];
const FOLLOWUP_OPTIONS = [2, 3, 5, 7, 10, 14, 21];
const dunningLabel = (d: number) => (d < 0 ? `${-d} ${d === -1 ? 'dia' : 'dias'} antes` : d === 0 ? 'No dia' : `${d} dias depois`);

/** Escolha de dias em "pastilhas" (mais claro do que uma lista escrita à mão). */
function DayPicker({ options, value, onChange, label, max, disabled }: { options: number[]; value: number[]; onChange: (v: number[]) => void; label: (d: number) => string; max: number; disabled?: boolean }) {
  const toggle = (d: number) => {
    if (value.includes(d)) { if (value.length > 1) onChange(value.filter((x) => x !== d)); }
    else if (value.length < max) onChange([...value, d].sort((a, b) => a - b));
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((d) => {
        const on = value.includes(d);
        return (
          <button key={d} type="button" disabled={disabled} aria-pressed={on} onClick={() => toggle(d)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition disabled:opacity-50 ${on ? 'border-brand-600 bg-brand-600 text-white' : 'border-[var(--border)] hover:bg-[var(--surface-2)]'}`}>
            {label(d)}
          </button>
        );
      })}
    </div>
  );
}

export function AutomationSettings() {
  const toast = useToast();
  const { can, session } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['automation-settings'], queryFn: () => get<{ settings: Settings }>('/automation/settings') });
  const [f, setF] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const editable = can('settings:edit');
  if (q.isLoading || !q.data) return <Loading />;
  const s = f ?? q.data.settings;
  const set = (patch: Partial<Settings>) => setF({ ...s, ...patch });
  const dirty = f !== null && JSON.stringify(f) !== JSON.stringify(q.data.settings);

  async function save(e: FormEvent) {
    e.preventDefault(); setBusy(true);
    try { await put('/automation/settings', s); toast('Automação guardada'); setF(null); qc.invalidateQueries({ queryKey: ['automation-settings'] }); }
    catch (ex) { toast(errorText(ex), 'error'); } finally { setBusy(false); }
  }
  async function sendDigest() {
    try { await post('/automation/digest/send'); toast('Resumo a caminho da sua caixa de correio'); } catch (ex) { toast(errorText(ex), 'error'); }
  }

  return (
    <form onSubmit={save} className="space-y-6">
      {session?.tenant.training_mode && (
        <Alert tone="info" title="Modo formação">Os lembretes automáticos também saem em modo formação, com os documentos de teste. Ligue-os quando estiver a facturar a clientes reais.</Alert>
      )}
      <fieldset disabled={!editable} className="space-y-6">
        <Card title="Cobrança automática" subtitle="Lembretes de pagamento ao cliente, por email, com a relação do que está em aberto em PDF">
          <div className="space-y-4">
            <Switch label="Enviar lembretes de pagamento" checked={s.autoDunning} onChange={(v) => set({ autoDunning: v })}
              hint="Um email por cliente e por etapa, para o email de facturação da ficha. Os dias úteis às 9h; nunca repete a mesma etapa." />
            <div className={s.autoDunning ? '' : 'pointer-events-none opacity-50'}>
              <div className="mb-2 text-xs font-medium text-[var(--muted)]">Quando, face ao vencimento (até 8)</div>
              <DayPicker options={DUNNING_OPTIONS} value={s.dunningDays} onChange={(v) => set({ dunningDays: v })} label={dunningLabel} max={8} disabled={!editable} />
            </div>
            <p className="text-xs text-[var(--muted)]">Clientes que não querem lembretes: desligue "Lembretes automáticos" na ficha do cliente. Uma factura paga deixa logo de ser lembrada.</p>
          </div>
        </Card>

        <Card title="Seguimento de pró-formas" subtitle="Ajuda a converter propostas em vendas">
          <div className="space-y-4">
            <Switch label="Acompanhar as pró-formas enviadas ao cliente" checked={s.quoteFollowup} onChange={(v) => set({ quoteFollowup: v })}
              hint="Pergunta ao cliente se tem dúvidas ou quer avançar, e avisa-o 2 dias antes de a proposta caducar. Pára quando a pró-forma dá factura." />
            <div className={s.quoteFollowup ? '' : 'pointer-events-none opacity-50'}>
              <div className="mb-2 text-xs font-medium text-[var(--muted)]">Dias depois do envio (até 5)</div>
              <DayPicker options={FOLLOWUP_OPTIONS} value={s.quoteFollowupDays} onChange={(v) => set({ quoteFollowupDays: v })} label={(d) => `${d} dias`} max={5} disabled={!editable} />
            </div>
          </div>
        </Card>

        <Card title="Avisos para a equipa" subtitle="Para o dono e os administradores">
          <div className="space-y-4">
            <Field label="Resumo por email" hint="Vendas, recebimentos, o que está vencido ou a vencer, pró-formas a caducar, stock baixo e prazos. Só sai quando há alguma coisa a dizer.">
              <Select value={s.digestFrequency} onChange={(e) => set({ digestFrequency: e.target.value as Settings['digestFrequency'] })}>
                <option value="daily">Todos os dias às 7h</option>
                <option value="weekly">À segunda-feira às 7h (resumo da semana)</option>
                <option value="off">Não enviar</option>
              </Select>
            </Field>
            <Switch label="Alerta de stock baixo" checked={s.stockAlerts} onChange={(v) => set({ stockAlerts: v })}
              hint="Email logo que um artigo desce abaixo do mínimo, com a quantidade a encomendar. Um aviso por descida." />
          </div>
        </Card>
      </fieldset>
      {editable && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button type="button" variant="secondary" onClick={sendDigest}><Mail className="size-4" />Enviar-me o resumo agora</Button>
          <Button type="submit" loading={busy} disabled={!dirty}>Guardar alterações</Button>
        </div>
      )}
    </form>
  );
}

// ═══ Avenças ════════════════════════════════════════════════════════════════

interface RecurringRow {
  id: string; name: string; doc_type: string; interval_months: number; next_run: string; end_date: string | null; mode: keyof typeof RECURRING_MODES;
  send_to: string | null; active: boolean; runs_count: number; last_run_at: string | null; last_error: string | null; last_document_id: string | null;
  last_number: string | null; customer_id: string; customer_name: string; template: { lines?: { quantity: string; unitPrice: string }[] };
}

const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Africa/Maputo' });
const intervalLabel = (n: number) => RECURRING_INTERVALS[n as keyof typeof RECURRING_INTERVALS] ?? `${n} meses`;

interface FormState { name: string; intervalMonths: string; nextRun: string; endDate: string; mode: keyof typeof RECURRING_MODES; sendTo: string; active: boolean }

function RecurringForm({ f, setF, errors, canIssue, canSend }: { f: FormState; setF: (f: FormState) => void; errors: Record<string, string>; canIssue: boolean; canSend: boolean }) {
  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Nome" required className="sm:col-span-2" error={errors.name}><Input value={f.name} onChange={set('name')} placeholder="Ex.: Manutenção mensal — Hotel Polana" /></Field>
      <Field label="Periodicidade" required>
        <Select value={f.intervalMonths} onChange={set('intervalMonths')}>
          {Object.entries(RECURRING_INTERVALS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
      </Field>
      <Field label="Próxima emissão" required error={errors.nextRun} hint="As seguintes repetem o mesmo dia do mês"><Input type="date" min={today()} value={f.nextRun} onChange={set('nextRun')} /></Field>
      <Field label="O que fazer na data" required className="sm:col-span-2">
        <Select value={f.mode} onChange={set('mode')}>
          {Object.entries(RECURRING_MODES).map(([v, l]) => <option key={v} value={v} disabled={(v !== 'draft' && !canIssue) || (v === 'issue_send' && !canSend)}>{l}</option>)}
        </Select>
      </Field>
      {f.mode === 'issue_send' && <Field label="Enviar para" required className="sm:col-span-2" error={errors.sendTo}><Input type="email" value={f.sendTo} onChange={set('sendTo')} /></Field>}
      <Field label="Termina em" hint="Vazio = sem fim" error={errors.endDate}><Input type="date" value={f.endDate} onChange={set('endDate')} /></Field>
      <p className="self-end pb-2 text-xs text-[var(--muted)] sm:col-span-1">
        Nas descrições pode usar <code>{'{mes}'}</code>, <code>{'{periodo}'}</code> e <code>{'{ano}'}</code> — ex.: "Avença de {'{mes}'}" sai "Avença de Novembro de 2026".
      </p>
    </div>
  );
}

const payload = (f: FormState) => ({ name: f.name, intervalMonths: Number(f.intervalMonths), nextRun: f.nextRun, endDate: f.endDate || null, mode: f.mode, sendTo: f.sendTo || null, active: f.active });

/** "Repetir" a partir de um documento (facturas, facturas-recibo, pró-formas). */
export function MakeRecurringModal({ open, onClose, doc }: { open: boolean; onClose: () => void; doc: { id: string; doc_type: string; full_number: string | null; customer_name: string | null; customer_email?: string | null; issue_date: string | null } & Record<string, any>; }) {
  const { can } = useAuth();
  const toast = useToast();
  const day = (doc.issue_date ?? today()).slice(8, 10);
  const nextMonth = (() => { const [y, m] = today().split('-').map(Number) as [number, number]; const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate(); return `${new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 8)}${String(Math.min(Number(day), last)).padStart(2, '0')}`; })();
  const [f, setF] = useState<FormState>({
    name: `${docLabel(doc.doc_type)} — ${doc.customer_name ?? 'cliente'}`, intervalMonths: '1', nextRun: nextMonth, endDate: '',
    mode: can('documents:issue') ? 'issue' : 'draft', sendTo: doc.customer_email ?? '', active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErrors({});
    try { await post('/recurring', { documentId: doc.id, ...payload(f) }); toast('Avença criada. Pode acompanhá-la em Vendas → Avenças.'); onClose(); }
    catch (ex) { setErrors(fieldErrors(ex)); toast(errorText(ex), 'error'); } finally { setBusy(false); }
  }
  return (
    <Modal open={open} onClose={onClose} title="Repetir este documento" subtitle="Avença: o Makila gera um documento igual na data marcada" icon={<Repeat className="size-5" />} size="lg"
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button form="recurring-new" type="submit" loading={busy}>Criar avença</Button></>}>
      <form id="recurring-new" onSubmit={submit}>
        <RecurringForm f={f} setF={setF} errors={errors} canIssue={can('documents:issue')} canSend={can('documents:send')} />
        <p className="mt-4 text-xs text-[var(--muted)]">Copia as linhas, preços, descontos e condições de {doc.full_number ?? 'este rascunho'}. Para mudar as linhas mais tarde, crie a avença de novo a partir de um documento actualizado.</p>
      </form>
    </Modal>
  );
}

function EditRecurringModal({ r, onClose, onSaved }: { r: RecurringRow; onClose: () => void; onSaved: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const [f, setF] = useState<FormState>({ name: r.name, intervalMonths: String(r.interval_months), nextRun: r.next_run, endDate: r.end_date ?? '', mode: r.mode, sendTo: r.send_to ?? '', active: r.active });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErrors({});
    try { await patch(`/recurring/${r.id}`, payload(f)); toast('Avença actualizada'); onSaved(); onClose(); }
    catch (ex) { setErrors(fieldErrors(ex)); toast(errorText(ex), 'error'); } finally { setBusy(false); }
  }
  return (
    <Modal open onClose={onClose} title={r.name} subtitle={`${docLabel(r.doc_type)} · ${r.customer_name}`} icon={<Repeat className="size-5" />} size="lg"
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button form="recurring-edit" type="submit" loading={busy}>Guardar</Button></>}>
      <form id="recurring-edit" onSubmit={submit}><RecurringForm f={f} setF={setF} errors={errors} canIssue={can('documents:issue')} canSend={can('documents:send')} /></form>
    </Modal>
  );
}

export function RecurringPage() {
  const { can } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['recurring'], queryFn: () => get<{ items: RecurringRow[] }>('/recurring') });
  const [editing, setEditing] = useState<RecurringRow | null>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['recurring'] }); qc.invalidateQueries({ queryKey: ['nav-counts'] }); };
  const amount = (r: RecurringRow) => (r.template.lines ?? []).reduce((s, l) => s + Number(l.quantity) * Number(l.unitPrice), 0);

  async function toggle(r: RecurringRow) {
    // Retomar uma avença parada há meses: a próxima data passa a hoje ou depois (o servidor não aceita datas passadas).
    const nextRun = r.active || r.next_run >= today() ? r.next_run : today();
    try { await patch(`/recurring/${r.id}`, { name: r.name, intervalMonths: r.interval_months, nextRun, endDate: r.end_date, mode: r.mode, sendTo: r.send_to, active: !r.active }); toast(r.active ? 'Avença suspensa' : 'Avença retomada'); refresh(); }
    catch (ex) { toast(errorText(ex), 'error'); }
  }
  async function runNow(r: RecurringRow) {
    if (!(await confirm({ title: 'Gerar agora?', description: `Gera já ${r.mode === 'draft' ? 'o rascunho' : 'o documento'} de "${r.name}". As seguintes contam a partir de hoje.`, confirmLabel: 'Gerar agora' }))) return;
    try { await post(`/recurring/${r.id}/run`); toast('A gerar… o documento aparece em segundos.'); setTimeout(refresh, 2500); } catch (ex) { toast(errorText(ex), 'error'); }
  }
  async function remove(r: RecurringRow) {
    if (!(await confirm({ title: 'Apagar a avença?', description: 'Os documentos já gerados mantêm-se. Deixa de se gerar novos.', confirmLabel: 'Apagar', tone: 'danger' }))) return;
    try { await del(`/recurring/${r.id}`); toast('Avença apagada'); refresh(); } catch (ex) { toast(errorText(ex), 'error'); }
  }

  const items = q.data?.items ?? [];
  const monthly = items.filter((r) => r.active).reduce((s, r) => s + amount(r) / r.interval_months, 0);
  return (
    <>
      <PageHeader eyebrow="Vendas" title="Avenças" subtitle="Documentos que se repetem: mensalidades, contratos de manutenção, rendas. O Makila gera-os sozinho na data marcada (às 6h)." />
      {items.some((r) => r.active && r.last_error) && (
        <Alert tone="error" title="Há avenças com problema" >Veja o motivo na coluna "Última". Se ficou um rascunho, pode emiti-lo à mão.</Alert>
      )}
      <Card className="mt-4" padded={false} title={items.length ? `${(() => { const n = items.filter((r) => r.active).length; return n === 1 ? '1 activa' : `${n} activas`; })()} · cerca de ${mt(monthly)} MT/mês (s/ IVA)` : undefined}>
        {q.isLoading ? <div className="p-5"><Loading /></div> : !items.length ? (
          <Empty icon={<Repeat className="size-6" />} title="Ainda sem avenças">
            Abra uma factura ou pró-forma que se repete e escolha <b>⋯ → Repetir este documento</b>.
          </Empty>
        ) : (
          <Table>
            <thead><tr><Th>Avença</Th><Th>Periodicidade</Th><Th>Próxima</Th><Th>Na data</Th><Th>Última</Th><Th className="text-right">Valor s/ IVA</Th><Th /></tr></thead>
            <tbody>{items.map((r) => (
              <tr key={r.id} className={r.active ? '' : 'opacity-60'}>
                <Td>
                  <button className="text-left font-medium hover:underline" onClick={() => setEditing(r)} disabled={!can('documents:create')}>{r.name}</button>
                  <div className="text-xs text-[var(--muted)]"><Link className="hover:underline" to={`/clientes/${r.customer_id}`}>{r.customer_name}</Link> · {docLabel(r.doc_type)}</div>
                </Td>
                <Td>{intervalLabel(r.interval_months)}{r.end_date && <div className="text-xs text-[var(--muted)]">até {date(r.end_date)}</div>}</Td>
                <Td>{r.active ? <span className="inline-flex items-center gap-1"><CalendarClock className="size-3.5 text-[var(--muted)]" />{date(r.next_run)}</span> : <Badge>Suspensa</Badge>}</Td>
                <Td className="text-xs">{RECURRING_MODES[r.mode]}{r.send_to && r.mode === 'issue_send' && <div className="text-[var(--muted)]">{r.send_to}</div>}</Td>
                <Td className="text-xs">
                  {r.last_error ? <span className="inline-flex items-start gap-1 text-red-700 dark:text-red-400"><AlertTriangle className="mt-0.5 size-3.5 shrink-0" />{r.last_error}</span>
                    : r.last_document_id ? <Link className="font-medium hover:underline" to={`/documentos/${r.last_document_id}`}>{r.last_number ?? 'Rascunho'}</Link> : <span className="text-[var(--muted)]">—</span>}
                  {r.last_run_at && <div className="text-[var(--muted)]">{dateTime(r.last_run_at)} · {r.runs_count}×</div>}
                </Td>
                <Td className="num">{mt(amount(r))}</Td>
                <Td className="whitespace-nowrap text-right">
                  {can('documents:create') && <>
                    <Button size="sm" variant="ghost" iconOnly aria-label="Gerar agora" title="Gerar agora" onClick={() => runNow(r)} disabled={!r.active} icon={<Send className="size-4" />} />
                    <Button size="sm" variant="ghost" iconOnly aria-label={r.active ? 'Suspender' : 'Retomar'} title={r.active ? 'Suspender' : 'Retomar'} onClick={() => toggle(r)} icon={r.active ? <Pause className="size-4" /> : <Play className="size-4" />} />
                  </>}
                  {can('documents:delete') && <Button size="sm" variant="ghost" iconOnly aria-label="Apagar" title="Apagar" onClick={() => remove(r)} icon={<Trash2 className="size-4" />} />}
                </Td>
              </tr>
            ))}</tbody>
          </Table>
        )}
      </Card>
      {editing && <EditRecurringModal r={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </>
  );
}

// ═══ Painel: o que precisa de atenção hoje ══════════════════════════════════

interface Agenda {
  today: string;
  overdue: { total: string; count: number; items: { id: string; full_number: string; name: string; pending: string; days: number }[] };
  dueSoon: { total: string; count: number; items: { id: string; full_number: string; name: string; pending: string; due_date: string }[] };
  quotes: { open: { n: number; v: string }; expiring: { id: string; full_number: string; name: string; phone: string | null; due_date: string; payable_total: string }[]; conversion90: { issued: number; converted: number } };
  lowStock: { id: string; code: string; name: string; qty: string; min_stock: string; unit: string }[];
  failedSends: { what: string; recipient: string; error: string | null }[];
  at: { period: string; deadline: string; daysLeft: number } | null;
}

/** Lista de tarefas do dia, calculada pelo mesmo código do resumo por email. */
export function TodayCard() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['agenda'], queryFn: () => get<Agenda>('/automation/agenda') });
  if (!q.data) return null;
  const a = q.data;
  const tasks: { icon: React.ReactNode; text: React.ReactNode; to: string; tone?: 'red' | 'amber' }[] = [];
  if (a.at) tasks.push({ icon: <Clock className="size-4" />, tone: a.at.daysLeft <= 3 ? 'red' : 'amber', to: '/comunicacao-at', text: <>Comunicar as facturas de <b>{a.at.period}</b> à AT até {date(a.at.deadline)}</> });
  for (const x of a.overdue.items.slice(0, 3)) tasks.push({ icon: <AlertTriangle className="size-4" />, tone: 'red', to: `/documentos/${x.id}`, text: <>Cobrar <b>{x.full_number}</b> a {x.name} — {mt(x.pending)} MT, vencida há {x.days} dias</> });
  if (a.overdue.count > 3) tasks.push({ icon: <AlertTriangle className="size-4" />, tone: 'red', to: '/contas-correntes', text: <>Mais {a.overdue.count - 3} documentos vencidos</> });
  for (const x of a.quotes.expiring) tasks.push({ icon: <BellRing className="size-4" />, tone: 'amber', to: `/documentos/${x.id}`, text: <>Ligar a {x.name}{x.phone ? ` (${x.phone})` : ''}: a pró-forma <b>{x.full_number}</b> de {mt(x.payable_total)} MT caduca a {date(x.due_date)}</> });
  if (a.lowStock.length && can('stock:view')) tasks.push({ icon: <AlertTriangle className="size-4" />, tone: 'amber', to: '/stock?alerta=1', text: <>Repor stock: {a.lowStock.slice(0, 3).map((p) => p.code).join(', ')}{a.lowStock.length > 3 ? ` e mais ${a.lowStock.length - 3}` : ''}</> });
  if (a.failedSends.length) tasks.push({ icon: <Mail className="size-4" />, tone: 'red', to: '/vendas', text: <>{a.failedSends.length} envio(s) por email falharam (ex.: {a.failedSends[0]!.what} para {a.failedSends[0]!.recipient})</> });
  if (a.dueSoon.count) tasks.push({ icon: <CalendarClock className="size-4" />, to: '/contas-correntes', text: <>{a.dueSoon.count} documento(s) vencem nos próximos 7 dias — {mt(a.dueSoon.total)} MT a receber</> });
  const conv = a.quotes.conversion90.issued ? Math.round((a.quotes.conversion90.converted / a.quotes.conversion90.issued) * 100) : null;
  return (
    <Card title="Para hoje" subtitle={tasks.length ? undefined : 'Nada pendente — está tudo em dia.'}
      actions={conv !== null && <span className="text-xs text-[var(--muted)]" title="Pró-formas emitidas nos últimos 90 dias que já deram factura ou venda">Conversão de pró-formas: <b>{conv}%</b></span>}>
      {tasks.length > 0 && (
        <ul className="divide-y divide-[var(--border)]">
          {tasks.map((t, i) => (
            <li key={i}>
              <Link to={t.to} className="flex items-start gap-3 py-2.5 text-sm hover:bg-[var(--surface-2)]">
                <span className={`mt-0.5 ${t.tone === 'red' ? 'text-red-600 dark:text-red-400' : t.tone === 'amber' ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--muted)]'}`}>{t.icon}</span>
                <span>{t.text}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
